import { scoreNumber } from "../../shared/score-number.js";
import type { ResultCard } from "./result-card.js";
import type {
  RoundFeedCard,
  RoundFeedSide,
} from "../../shared/contracts/round-feed.js";
import {
  posterDate,
  posterScore,
} from "../../shared/presentation/round-poster-score.js";
import {
  avatarCircle,
  avatarImage,
  posterColors as c,
  textLine,
  textLines,
} from "./poster-canvas-tools.js";

/** App-native graphic, using the same score labels and story as the on-screen poster. */
export async function renderRoundPoster(
  card: ResultCard,
): Promise<HTMLCanvasElement> {
  const { round, avatarUrls } = card.poster!;
  const canvas = document.createElement("canvas");
  canvas.width = 1080;
  const posterHeight =
    round.sides.length <= 2 ? 1160 : 660 + round.sides.length * 205;
  canvas.height = posterHeight + (card.memory ? 320 : 0);
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("The result image could not be drawn.");
  const glow = ctx.createLinearGradient(0, 0, 1080, canvas.height);
  glow.addColorStop(0, "#29382a");
  glow.addColorStop(0.45, c.dark);
  glow.addColorStop(1, "#04100c");
  ctx.fillStyle = glow;
  ctx.fillRect(0, 0, 1080, canvas.height);
  ctx.strokeStyle = c.gold;
  ctx.lineWidth = 2;
  ctx.strokeRect(35, 35, 1010, canvas.height - 70);
  textLine(ctx, "FAIRWAY RIVALRY", 90, 112, 900, 32, c.gold, "left", false);
  textLine(ctx, round.format, 90, 171, 570, 29, c.muted, "left", false);
  textLine(ctx, posterDate(round), 990, 171, 340, 27, c.muted, "right", false);
  const images = await Promise.all(
    round.sides.map((side) =>
      avatarImage(
        side.participantIds.length === 1
          ? (avatarUrls[side.participantIds[0]!] ?? null)
          : null,
      ),
    ),
  );
  if (round.sides.length === 1) drawSolo(ctx, round, images[0]!);
  else if (round.sides.length === 2) drawDuel(ctx, round, images);
  else
    round.sides.forEach((side, i) =>
      drawFlightRow(ctx, round, side, images[i]!, 250 + i * 205),
    );
  drawPosterFooter(ctx, card, canvas.height, posterHeight);
  return canvas;
}
function drawFlightRow(
  ctx: CanvasRenderingContext2D,
  round: RoundFeedCard,
  side: RoundFeedSide,
  image: HTMLImageElement | null,
  y: number,
) {
  const won = round.winnerIds.includes(side.id);
  avatarCircle(ctx, image, side.name, 90, y, 86);
  textLine(ctx, side.name, 202, y + 34, 570, 36, c.cream);
  const strokes =
    side.strokes === null
      ? "Strokes unknown"
      : `${scoreNumber(side.strokes)} strokes${side.incompleteStrokes ? "*" : ""}`;
  const par =
    side.versusPar === null
      ? ""
      : side.versusPar === 0
        ? "E"
        : side.versusPar > 0
          ? `+${scoreNumber(side.versusPar)}`
          : scoreNumber(side.versusPar);
  textLine(
    ctx,
    `${strokes}${par ? ` · ${par} par` : ""}`,
    202,
    y + 77,
    570,
    27,
    c.blue,
    "left",
    false,
  );
  if (won)
    textLine(ctx, "WINNER", 202, y + 118, 550, 23, c.gold, "left", false);
  textLine(ctx, scoreNumber(side.value), 985, y + 60, 185, 70, c.gold, "right");
  textLine(
    ctx,
    round.kind === "stableford" ? "points" : `${round.basis} strokes`,
    985,
    y + 101,
    200,
    25,
    c.muted,
    "right",
    false,
  );
  ctx.strokeStyle = "#4b5945";
  ctx.beginPath();
  ctx.moveTo(90, y + 160);
  ctx.lineTo(990, y + 160);
  ctx.stroke();
}
function drawSolo(
  ctx: CanvasRenderingContext2D,
  round: RoundFeedCard,
  image: HTMLImageElement | null,
) {
  const side = round.sides[0]!;
  avatarCircle(ctx, image, side.name, 462, 265, 156);
  textLine(ctx, side.name, 540, 482, 900, 48, c.cream, "center");
  drawPlayerScore(ctx, round, side, {
    x: 540,
    top: 545,
    width: 900,
    align: "center",
    scale: 1,
    compact: false,
  });
}
function drawDuel(
  ctx: CanvasRenderingContext2D,
  round: RoundFeedCard,
  images: Array<HTMLImageElement | null>,
) {
  drawPlayer(ctx, round, round.sides[0]!, images[0]!, 95, 255, false);
  drawPlayer(ctx, round, round.sides[1]!, images[1]!, 985, 255, true);
}
function drawPlayer(
  ctx: CanvasRenderingContext2D,
  round: RoundFeedCard,
  side: RoundFeedSide,
  image: HTMLImageElement | null,
  x: number,
  y: number,
  right: boolean,
  compact?: number,
) {
  const scale = compact ?? 1;
  const width = compact ? 400 : 440,
    size = (compact ? 80 : 128) * scale;
  const align = right ? "right" : "left";
  avatarCircle(ctx, image, side.name, right ? x - size : x, y, size);
  textLine(
    ctx,
    side.name,
    x,
    y + size + 57 * scale,
    width,
    (compact ? 34 : 48) * scale,
    c.cream,
    align,
  );
  const won = round.winnerIds.length === 1 && round.winnerIds.includes(side.id);
  if (won && round.sides.length > 1)
    textLine(
      ctx,
      "WINNER",
      x,
      y + size + 88 * scale,
      width,
      21 * scale,
      c.gold,
      align,
      false,
    );
  drawPlayerScore(ctx, round, side, {
    x,
    top: y + size + 132 * scale,
    width,
    align,
    scale,
    compact: Boolean(compact),
  });
}

function drawPosterFooter(
  ctx: CanvasRenderingContext2D,
  card: ResultCard,
  height: number,
  posterHeight: number,
) {
  const round = card.poster!.round;
  textLine(ctx, round.outcome, 90, posterHeight - 360, 900, 40, c.gold);
  textLines(
    ctx,
    [round.stakeStory, round.story?.impact, round.story?.turningPoint]
      .filter(Boolean)
      .join(" "),
    90,
    posterHeight - 300,
    900,
    28,
    3,
  );
  if (round.story?.record)
    textLine(
      ctx,
      round.story.record,
      90,
      posterHeight - 185,
      900,
      27,
      c.muted,
      "left",
      false,
    );
  if (round.story?.record)
    textLine(
      ctx,
      "Shared record through this round · real rounds only",
      90,
      posterHeight - 155,
      900,
      18,
      c.muted,
      "left",
      false,
    );
  textLine(ctx, round.courseName, 90, posterHeight - 110, 900, 34, c.cream);
  if (card.memory) {
    textLine(
      ctx,
      `Your memory · result version ${card.memory.roundVersion}`,
      90,
      posterHeight - 50,
      900,
      25,
      c.gold,
      "left",
      false,
    );
    textLines(ctx, card.memory.text, 90, posterHeight, 900, 30, 5);
  }
  textLine(
    ctx,
    "Every rivalry starts with one round. · fairwayrivalry.com",
    90,
    height - 60,
    900,
    26,
    c.gold,
    "left",
    false,
  );
}

type ScorePosition = {
  x: number;
  top: number;
  width: number;
  align: CanvasTextAlign;
  scale: number;
  compact: boolean;
};
function drawPlayerScore(
  ctx: CanvasRenderingContext2D,
  round: RoundFeedCard,
  side: RoundFeedSide,
  position: ScorePosition,
) {
  const { x, top, width, align, scale, compact } = position;
  const score = posterScore(round, side);
  if (score.above)
    textLine(
      ctx,
      score.above,
      x,
      top,
      width,
      24 * scale,
      c.muted,
      align,
      false,
    );
  if (score.par)
    textLine(
      ctx,
      score.par,
      x,
      top + (score.above ? 32 : 0) * scale,
      width,
      29 * scale,
      c.blue,
      align,
      false,
    );
  const valueY = top + (score.above ? 125 : 98) * scale;
  textLine(
    ctx,
    score.value,
    x,
    valueY,
    width,
    (compact ? 60 : 102) * scale,
    c.gold,
    align,
  );
  textLine(
    ctx,
    score.label,
    x,
    valueY + 30 * scale,
    width,
    24 * scale,
    c.muted,
    align,
    false,
  );
  if (score.below)
    textLine(
      ctx,
      score.below,
      x,
      valueY + 62 * scale,
      width,
      23 * scale,
      c.muted,
      align,
      false,
    );
}
